import { useEffect, useState, useMemo, useCallback } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format, parseISO } from 'date-fns';
import logo from '../../../../img/logo/Logo_FICSULLANA.png';
import { toast } from 'react-toastify';
import { getBase64FromImage } from '../../../../utilities/pdfLogo';

const CONSTANTES = {
    MARGENES: {
        PREDETERMINADO: 15,
        TABLA: 20,
        DESCARGO: 20
    },
    FUENTES: {
        TAMANIOS: {
            PEQUENIO: 6,
            NORMAL: 8,
            MEDIANO: 10,
            GRANDE: 12,
            MUY_GRANDE: 14,
            EXTRA_GRANDE: 17
        }
    },
    COLORES: {
        NEGRO: [0, 0, 0],
        GRIS_60: [60, 60, 60],
        GRIS_80: [80, 80, 80],
        GRIS_100: [100, 100, 100],
        GRIS_CLARO: [200, 200, 200],
        GRIS_MUY_CLARO: [245, 245, 245]
    },
    INFORMACION_CUENTA: {
        CTA: '5357079899068',
        CCI: '0025350070989906838'
    }
};

const DetallePrestamoCuotas = ({ cuotas, selectedClient, selectedLoan, clients, loans, asesores, selectedAsesor, onComplete, onPDFGenerated }) => {
    const [estaGenerando, setEstaGenerando] = useState(false);

    const datosMemoizados = useMemo(() => {
        const selectedClientData = clients.find(client => client.idUsuario === selectedClient);
        const selectedLoanData = loans.find(loan => loan.idPrestamo === selectedLoan);
        
        return {
            cliente: selectedClientData,
            prestamo: selectedLoanData
        };
    }, [clients, selectedClient, loans, selectedLoan]);

    const formatearFecha = useCallback((cadenaFecha) => {
        if (!cadenaFecha) return '-';
        try {
            return format(parseISO(cadenaFecha), 'dd/MM/yyyy');
        } catch {
            try {
                return format(new Date(cadenaFecha), 'dd/MM/yyyy');
            } catch {
                return '-';
            }
        }
    }, []);

    const agregarPrefijoYCerosIzquierda = useCallback((id, prefijo, longitudTotal) => {
        const idStr = String(id);
        const cerosNecesarios = longitudTotal - (prefijo.length + idStr.length);
        return prefijo + '0'.repeat(Math.max(0, cerosNecesarios)) + idStr;
    }, []);

    const dividirTextoEnLineas = useCallback((texto, longitudMaxima) => {
        if (!texto) return ['-'];
        const palabras = String(texto).split(' ');
        const lineas = [];
        let lineaActual = '';

        palabras.forEach((palabra) => {
            if (lineaActual.length + palabra.length + 1 <= longitudMaxima) {
                lineaActual += (lineaActual ? ' ' : '') + palabra;
            } else {
                if (lineaActual) lineas.push(lineaActual);
                lineaActual = palabra;
            }
        });

        if (lineaActual) lineas.push(lineaActual);
        return lineas;
    }, []);

    const establecerFuenteDocumento = useCallback((doc, estilo = 'normal', tamanio = CONSTANTES.FUENTES.TAMANIOS.NORMAL) => {
        doc.setFont('helvetica', estilo);
        doc.setFontSize(tamanio);
    }, []);

    const establecerColorTexto = useCallback((doc, color) => {
        doc.setTextColor(...color);
    }, []);

    const agregarLogo = useCallback(async (doc, x = CONSTANTES.MARGENES.PREDETERMINADO, y = 2) => {
        try {
            const logoBase64 = await getBase64FromImage(logo);
            doc.addImage(logoBase64, 'PNG', x, y, 55, 58);

            establecerFuenteDocumento(doc, 'normal', CONSTANTES.FUENTES.TAMANIOS.NORMAL);
            establecerColorTexto(doc, CONSTANTES.COLORES.NEGRO);

            const logoTextoX = x + 27.5;
            doc.text('CORPORACIÓN E INVERSIONES', logoTextoX, y + 38, { align: 'center' });
            doc.text('SULLANA LA PERLA DEL CHIRA S.A.C', logoTextoX, y + 42, { align: 'center' });

            return y + 70;
        } catch (error) {
            console.warn('Error loading logo:', error);
            return y + 20;
        }
    }, [establecerFuenteDocumento, establecerColorTexto]);

    const dibujarBloquePago = useCallback((doc, inicioY) => {
        const anchoPagina = doc.internal.pageSize.getWidth();
        const margen = CONSTANTES.MARGENES.PREDETERMINADO;
        let y = inicioY;

        establecerFuenteDocumento(doc, 'normal', 7.8);
        establecerColorTexto(doc, CONSTANTES.COLORES.GRIS_60);

        doc.text(
            'Paga tu crédito en los siguientes agentes corresponsales y solo transferencia a la cuenta BCP:',
            margen, y
        );
        y += 4;
        doc.text(
            `CTA N° ${CONSTANTES.INFORMACION_CUENTA.CTA} / CCI N° ${CONSTANTES.INFORMACION_CUENTA.CCI}`,
            margen, y
        );
        y += 3;

        const anchoCaja = (anchoPagina - 2 * margen - 5) / 2;
        const altoCaja = 18;
        const cajaY = y;

        doc.setDrawColor(...CONSTANTES.COLORES.GRIS_80);
        doc.setLineWidth(0.4);

        const caja1Centro = margen + anchoCaja / 2;
        doc.rect(margen, cajaY, anchoCaja, altoCaja);
        establecerFuenteDocumento(doc, 'bold', 9);
        establecerColorTexto(doc, [20, 20, 20]);
        doc.text('CENTRO DE PAGO AUTORIZADO AGENTE', caja1Centro, cajaY + 6, { align: 'center' });
        doc.text('SERVITEL SANCHEZ - QUERECOTILLO PLIN', caja1Centro, cajaY + 11, { align: 'center' });
        establecerFuenteDocumento(doc, 'normal', 9);
        doc.text('N° 938176381', caja1Centro, cajaY + 16, { align: 'center' });

        const caja2X = margen + anchoCaja + 5;
        const caja2Centro = caja2X + anchoCaja / 2;
        doc.rect(caja2X, cajaY, anchoCaja, altoCaja);
        establecerFuenteDocumento(doc, 'bold', 9);
        doc.text('TRANSFERENCIA A LA CUENTA BCP', caja2Centro, cajaY + 6, { align: 'center' });
        establecerFuenteDocumento(doc, 'normal', 9);
        doc.text(CONSTANTES.INFORMACION_CUENTA.CTA, caja2Centro, cajaY + 11, { align: 'center' });
        doc.text(`N°${CONSTANTES.INFORMACION_CUENTA.CCI}`, caja2Centro, cajaY + 16, { align: 'center' });

        return cajaY + altoCaja;
    }, [establecerFuenteDocumento, establecerColorTexto]);

    const generarPDF = useCallback(async () => {
        setEstaGenerando(true);

        try {
            const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
            const anchoPagina = doc.internal.pageSize.getWidth();
            let y = await agregarLogo(doc);
            const textoX = anchoPagina - CONSTANTES.MARGENES.PREDETERMINADO;
            const encabezadoY = 27;
            establecerFuenteDocumento(doc, 'bold', CONSTANTES.FUENTES.TAMANIOS.GRANDE);
            establecerColorTexto(doc, CONSTANTES.COLORES.NEGRO);
            doc.text('CRONOGRAMA DE PAGOS', textoX, encabezadoY + 6, { align: 'right' });
            doc.text('[REPORTE DE CUOTAS]', textoX, encabezadoY + 12, { align: 'right' });
            y = encabezadoY + 26;

            const col1X = CONSTANTES.MARGENES.PREDETERMINADO;
            const col2X = anchoPagina / 2 + 5;

            const datosIzquierda = [
                { etiqueta: 'FECHA', valor: formatearFecha(datosMemoizados.prestamo?.fecha_inicio) },
                { etiqueta: 'CÓD. TITULAR', valor: datosMemoizados.cliente?.idUsuario || '-' },
                { etiqueta: 'TITULAR', valor: datosMemoizados.cliente ? `${datosMemoizados.cliente.nombre} ${datosMemoizados.cliente.apellidoPaterno} ${datosMemoizados.cliente.apellidoMaterno}` : '-' },
                { etiqueta: 'DNI', valor: datosMemoizados.cliente?.dni || '-' },
                { etiqueta: 'CUENTA BANCARIA', valor: datosMemoizados.cliente?.cuentasBancarias?.length ? datosMemoizados.cliente.cuentasBancarias[0].numeroCuenta : '-' },
                { etiqueta: 'PRODUCTO', valor: datosMemoizados.prestamo?.producto || datosMemoizados.prestamo?.idProducto || '-' },
            ];

            const datosDerecha = [
                { etiqueta: 'ANALISTA', valor: datosMemoizados.prestamo?.asesor || '-' },
                {
                    etiqueta: 'CÓD. PRÉSTAMO',
                    valor: datosMemoizados.prestamo?.idPrestamo
                        ? agregarPrefijoYCerosIzquierda(datosMemoizados.prestamo.idPrestamo, '79', 10)
                        : '-',
                },
                { etiqueta: 'MONEDA', valor: 'SOLES' },
                { etiqueta: 'MONTO', valor: datosMemoizados.prestamo?.monto ? `S/ ${parseFloat(datosMemoizados.prestamo.monto).toFixed(2)}` : '-' },
                { etiqueta: 'PERIODICIDAD', valor: datosMemoizados.prestamo?.frecuencia?.toUpperCase() || '-' },
                { etiqueta: 'N° CUOTAS', valor: datosMemoizados.prestamo?.cuotas || '-' },
                { etiqueta: 'TASA DE INTERÉS', valor: datosMemoizados.prestamo?.interes ? `${datosMemoizados.prestamo.interes}%` : '-' },
                { etiqueta: 'TOTAL A PAGAR', valor: datosMemoizados.prestamo?.total ? `S/ ${parseFloat(datosMemoizados.prestamo.total).toFixed(2)}` : '-' },
            ];

            const renderizarDatosColumna = (datos, colX, inicioY) => {
                let posY = inicioY;
                datos.forEach(({ etiqueta, valor }) => {
                    establecerFuenteDocumento(doc, 'bold', CONSTANTES.FUENTES.TAMANIOS.NORMAL);
                    establecerColorTexto(doc, CONSTANTES.COLORES.NEGRO);
                    doc.text(`${etiqueta}:`, colX, posY);
                    establecerFuenteDocumento(doc, 'normal', CONSTANTES.FUENTES.TAMANIOS.NORMAL);

                    if (etiqueta === 'TITULAR' && valor) {
                        const lineas = dividirTextoEnLineas(valor, 33);
                        lineas.forEach((linea) => {
                            doc.text(linea, colX + 35, posY);
                            posY += 6;
                        });
                    } else {
                        doc.text(String(valor), colX + 35, posY);
                        posY += 6;
                    }
                });
                return posY;
            };

            const izquierdaY = renderizarDatosColumna(datosIzquierda, col1X, y);
            const derechaY = renderizarDatosColumna(datosDerecha, col2X, y);
            y = Math.max(izquierdaY, derechaY) + 3;

            const encabezados = ['N°', 'F. VENC.', 'F. PAGO', 'CAPITAL', 'INTERÉS', 'DÍAS MORA', 'MORA', 'OTROS', 'CUOTA', 'ESTADO'];
            const filas = (cuotas || []).map((cuota) => [
                String(cuota.numero_cuota || ''),
                formatearFecha(cuota.fecha_vencimiento),
                cuota.pago_completo ? formatearFecha(cuota.fecha_pago_total) : '-',
                String(cuota.capital ? Number(cuota.capital).toFixed(2) : '0.00'),
                String(cuota.interes ? Number(cuota.interes).toFixed(2) : '0.00'),
                String(cuota.dias_mora || '0'),
                cuota.mora_reducida > 0
                    ? `${Number(cuota.mora || 0).toFixed(2)} (-${cuota.mora_reducida}%)`
                    : String(Number(cuota.mora || 0).toFixed(2)),
                String(cuota.otros ? Number(cuota.otros).toFixed(2) : '0.00'),
                String(cuota.monto ? Number(cuota.monto).toFixed(2) : '0.00'),
                String(cuota.estado?.toUpperCase() || 'PENDIENTE')
            ]);

            const totales = cuotas.reduce((acc, cuota) => {
                acc.capital += Number(cuota.capital || 0);
                acc.interes += Number(cuota.interes || 0);
                acc.mora += Number(cuota.mora || 0);
                acc.otros += Number(cuota.otros || 0);
                acc.monto += Number(cuota.monto || 0);
                acc.diasMora += Number(cuota.dias_mora || 0);
                return acc;
            }, { capital: 0, interes: 0, mora: 0, otros: 0, monto: 0, diasMora: 0 });

            filas.push([
                '',
                'TOTALES',
                '',
                totales.capital.toFixed(2),
                totales.interes.toFixed(2),
                totales.diasMora.toString(),
                totales.mora.toFixed(2),
                totales.otros.toFixed(2),
                totales.monto.toFixed(2),
                ''
            ]);

            autoTable(doc, {
                startY: y,
                head: [encabezados],
                body: filas,
                margin: { left: CONSTANTES.MARGENES.TABLA, right: CONSTANTES.MARGENES.TABLA },
                tableWidth: 'auto',
                headStyles: {
                    fillColor: [230, 230, 230],
                    textColor: CONSTANTES.COLORES.NEGRO,
                    fontSize: 8,
                    fontStyle: 'bold',
                    halign: 'center',
                },
                bodyStyles: {
                    fontSize: 8,
                    textColor: CONSTANTES.COLORES.NEGRO,
                    halign: 'center',
                },
                alternateRowStyles: {
                    fillColor: CONSTANTES.COLORES.GRIS_MUY_CLARO,
                },
                columnStyles: {
                    0: { halign: 'center' },
                    1: { halign: 'center' },
                    2: { halign: 'center' },
                    3: { halign: 'center' },
                    4: { halign: 'center' },
                    5: { halign: 'center' },
                    6: { halign: 'center' },
                    7: { halign: 'center' },
                    8: { halign: 'center' },
                    9: { halign: 'center' },
                },
                didParseCell: function (datos) {
                    if (datos.row.index === filas.length - 1) {
                        datos.cell.styles.fontStyle = 'bold';
                        datos.cell.styles.fillColor = CONSTANTES.COLORES.GRIS_CLARO;
                    }
                },
            });

            y = doc.lastAutoTable.finalY + 5;

            const margenDescargo = CONSTANTES.MARGENES.DESCARGO;
            const anchoDescargo = anchoPagina - 2 * margenDescargo;
            establecerFuenteDocumento(doc, 'normal', CONSTANTES.FUENTES.TAMANIOS.NORMAL);
            establecerColorTexto(doc, [70, 70, 70]);
            const descargo = 'EL (LOS) CLIENTE(S) declara(n) expresamente que previamente a la celebración del contrato han recibido la información sobre las condiciones de su crédito (Tasa de interés compensatorio, comisiones y penalidades); copia de título valor, contrato de crédito por lo que firma en señal de conformidad.';
            doc.text(descargo, margenDescargo, y, { maxWidth: anchoDescargo });

            y += 30;

            const firmaX = CONSTANTES.MARGENES.PREDETERMINADO;
            const anchoFirma = 70;
            const lineaFirmaY = y + 5;

            doc.setDrawColor(40);
            doc.setLineWidth(0.3);
            doc.line(firmaX, lineaFirmaY, firmaX + anchoFirma, lineaFirmaY);

            let datosY = lineaFirmaY + 5;
            const espaciadoLinea = 4.5;
            establecerFuenteDocumento(doc, 'normal', 8.2);

            const dibujarDato = (etiqueta, valor) => {
                establecerFuenteDocumento(doc, 'bold', 8.2);
                doc.text(`${etiqueta}:`, firmaX, datosY);
                establecerFuenteDocumento(doc, 'normal', 8.2);
                doc.text(valor || '-', firmaX + 20, datosY);
                datosY += espaciadoLinea;
            };

            dibujarDato('TITULAR', datosMemoizados.cliente ? `${datosMemoizados.cliente.nombre} ${datosMemoizados.cliente.apellidoPaterno} ${datosMemoizados.cliente.apellidoMaterno}` : '-');
            dibujarDato('DNI', datosMemoizados.cliente?.dni);

            establecerFuenteDocumento(doc, 'bold', 8.2);
            establecerColorTexto(doc, CONSTANTES.COLORES.NEGRO);
            doc.text('CONTACTO:', firmaX, datosY);
            datosY += espaciadoLinea;

            y = datosY + 10;
            const ultimoY = dibujarBloquePago(doc, y);

            establecerFuenteDocumento(doc, 'italic', 7);
            establecerColorTexto(doc, CONSTANTES.COLORES.GRIS_100);
            const fechaHoy = format(new Date(), 'dd/MM/yyyy');
            doc.text(
                `Fecha de emisión de reporte: ${fechaHoy}`,
                anchoPagina / 2,
                ultimoY + 8,
                { align: 'center' }
            );

            const pdfBlob = doc.output('blob');
            const pdfUrl = URL.createObjectURL(pdfBlob);

            if (onPDFGenerated) {
                onPDFGenerated(pdfUrl);
            }

            if (onComplete) {
                onComplete();
            }

        } catch (error) {
            console.error('Error generando PDF:', error);
            toast.error('Error al generar el PDF');
            if (onComplete) {
                onComplete();
            }
        } finally {
            setEstaGenerando(false);
        }
    }, [
        cuotas,
        datosMemoizados,
        formatearFecha,
        agregarPrefijoYCerosIzquierda,
        dividirTextoEnLineas,
        agregarLogo,
        dibujarBloquePago,
        establecerFuenteDocumento,
        establecerColorTexto,
        onPDFGenerated,
        onComplete
    ]);

    useEffect(() => {
        if (!estaGenerando) {
            generarPDF();
        }
    }, [generarPDF, estaGenerando]);

    return estaGenerando ? <div>Generando cronograma de cuotas...</div> : null;
};

export default DetallePrestamoCuotas;
